/**
 * Reset Password Page
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Input,
  Label,
  Button,
} from "@jourfx/ui";
import { Loader2, CheckCircle, AlertTriangle } from "lucide-react";
import { useBrandLogoUrl } from "@/branding/BrandingProvider";

export const Route = createFileRoute("/reset-password/$token")({
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  // Force light mode on auth pages
  useEffect(() => {
    const root = document.documentElement;
    const wasDark = root.classList.contains("dark");
    root.classList.remove("dark");
    root.classList.add("light");
    return () => {
      if (wasDark) {
        root.classList.remove("light");
        root.classList.add("dark");
      }
    };
  }, []);

  const primaryLogoUrl = useBrandLogoUrl("primary");
  const { token } = Route.useParams();
  const validate = useQuery(api.auth.passwordReset.validate, { token });
  const complete = useMutation(api.auth.passwordReset.complete);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError("Die Passwörter stimmen nicht überein.");
      return;
    }

    setIsLoading(true);
    try {
      await complete({ token, newPassword });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Zurücksetzen");
    } finally {
      setIsLoading(false);
    }
  };

  if (validate === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!validate.valid && !success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <AlertTriangle className="mx-auto h-10 w-10 text-amber-500 mb-2" />
            <CardTitle>Link ungültig</CardTitle>
            <CardDescription>
              Der Reset-Link ist ungültig oder abgelaufen.
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex justify-center">
            <Link to="/forgot-password" className="text-sm text-primary hover:underline">
              Neuen Link anfordern
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-12 bg-[hsl(145_15%_95%)]">
      <div className="relative z-10 w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <img src={primaryLogoUrl} alt="JourFx" className="h-10 w-auto" />
        </div>
        <Card className="border border-border/50 bg-card shadow-xl">
          <CardHeader className="text-center">
            <CardTitle>Passwort zurücksetzen</CardTitle>
            <CardDescription>
              Bitte wählen Sie ein neues Passwort für {validate.email}.
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {success ? (
                <div className="flex flex-col items-center gap-2 text-center text-emerald-600">
                  <CheckCircle className="h-8 w-8" />
                  <p className="text-sm text-muted-foreground">
                    Passwort erfolgreich geändert. Sie können sich jetzt anmelden.
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="new-password">Neues Passwort</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={12}
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Passwort bestätigen</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                    />
                  </div>
                  {error && (
                    <p className="text-sm text-destructive">{error}</p>
                  )}
                </>
              )}
            </CardContent>
            <CardFooter className="flex flex-col gap-3">
              {!success && (
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Passwort setzen
                </Button>
              )}
              <Link
                to="/login"
                search={{ redirect: "/dashboard" }}
                className="text-sm text-primary hover:underline"
              >
                Zurück zur Anmeldung
              </Link>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}
